import Gallery from '../gallery';
import DeliveryDetails from './components/delivery-details';
import BundleProducts from './components/BundleProducts';
import ProductSpecificationDetails from './components/Product-specification';
import FeaturesBenefits from './components/features-benefits';
import Header from './components/header';
import VariantSelector from './components/variant-selector';
import { ProductDetailsProps } from './types';
import { useState, useMemo, useCallback } from 'react';
import { useParams } from 'next/navigation';
import SidebarPDP from './components/sidebar';
import SidebarMobilePDP from './components/sidebar-mobile';
import SpareParts from './components/spare-parts';
import Spinner from '../spinner';
import Loader from '../loader';
import useMediaQuery from '@/hooks/useMediaQuery';
import { desktop } from '@/constants/screensizes';
import ProductSliderAlgolia from '../product-slider-algolia';
import useProduct from '@/lib/hooks/useProduct';
import AddProductToProject from '../project-builder/add-prod-to-project';
import CompatibilityProducts from './components/compatibility-products';
import useAccount from '@/lib/hooks/useAccount';
import { stateDeafaultWarehouseLocations } from '@/constants/inventoryAvailability';

const ProductDetails = ({
  product,
  variant,
  variants,
  handleColorClick,
  customerQuantityPrice,
  inventoryData,
  componentsProducts,
  deliveryEstimate,
  isLoading,
  isBundleLoading,
  isPriceLoading,
  setQuantity,
  quantityPrice,
  quantity,
  quantityPriceLoading,
  setPostalCode,
  postalCode,
  loadingDeliverEstimate
}: ProductDetailsProps) => {
  const { account } = useAccount();
let warehouse = deliveryEstimate?.CustDefaultWarehouse;

if (deliveryEstimate?.CentralStore === "Yes") {
  warehouse = deliveryEstimate?.CentralStoreDefaultWarehouse;
} else if (deliveryEstimate?.DualStore === "Yes") {
  warehouse = deliveryEstimate?.DualStoreDefaultWarehouse;
}
if (!warehouse?.trim()) {
  warehouse = stateDeafaultWarehouseLocations[account?.state ?? ""];
}

  const warehouseqty =deliveryEstimate?.qtyAvailable;
  const [loading, setLoading] = useState(false);
  const [isDesktop] = useMediaQuery(desktop);
  const { locale } = useParams();
  const itemCode = variant?.attributes?.['item-code'];
  const rawItemName = variant?.attributes?.['item-name'];
  const itemName =
    rawItemName && typeof rawItemName === 'object'
      ? rawItemName[locale === 'au' ? 'en-AU' : 'en-NZ'] ?? product?.name
      : rawItemName ?? product?.name;
  const isBuilderRange = variant?.attributes?.['builder-range'].toLocaleLowerCase() === 'yes';
  const isPrototype = product?.lifecycle?.toUpperCase()?.trim() === 'PROTOTYPE';
  const [openAddToProject, setOpenAddToProject] = useState<boolean>(false);
  const [productToAdd, setProductToAdd] = useState<any>(null);

  const handleCancelAddProduct = () => {
    setOpenAddToProject(false);
    setProductToAdd(null);
  };
  const localeString = Array.isArray(locale) ? locale[0] : locale || '';

  const filterByLocale = useCallback(
    (refs: string[]): string[] => {
      const countrySuffix = localeString.toUpperCase(); // 'AU' or 'NZ'
      return refs
        .filter((ref: string) => {
          const pipeIndex = ref.indexOf('|');
          if (pipeIndex === -1) return true; // no suffix — include as-is (backwards compat)
          const suffix = ref.slice(pipeIndex + 1); // e.g. 'AU01', 'AU', 'NZ01', 'NZ'
          return suffix.toUpperCase().startsWith(countrySuffix);
        })
        .map((ref: string) => ref.split('|')[0]);
    },
    [localeString],
  );

  const componentsReferences = useMemo(
    () =>
      filterByLocale(
        variant?.attributes?.['item-attributes']?.find(
          (attr: { name: string }) => attr.name === 'bundle-references',
        )?.value ?? [],
      ),
    [variant, filterByLocale],
  );
  
  const sparePartsReferences = useMemo(
    () =>
      filterByLocale(
        variant?.attributes?.['item-attributes']?.find(
          (attr: { name: string }) => attr.name === 'spare-references',
        )?.value ?? [],
      ),
    [variant, filterByLocale],
  );

  const toiletSeatPancompatibilityReferences = useMemo(
    () =>
      filterByLocale(
        variant?.attributes?.['item-attributes']?.find(
          (attr: { name: string }) => attr.name === 'toilet-seatpan-recommnded-references',
        )?.value ?? [],
      ),
    [variant, filterByLocale],
  );

  const toiletSeatPanSuitableReferences = useMemo(
    () =>
      filterByLocale(
        variant?.attributes?.['item-attributes']?.find(
          (attr: { name: string }) => attr.name === 'toilet-seatpan-suitable-references',
        )?.value ?? [],
      ),
    [variant, filterByLocale],
  );

  const productType = variant?.attributes?.['product-types']?.includes('Brochures');
  if (productType) {
    const currentUrl = new URL(window.location.href);
    if (!currentUrl.searchParams.has('b')) {
      currentUrl.search += (currentUrl.search ? '&' : '?') + 'b';
      window.history.pushState({}, '', currentUrl.toString());
    }
  }
  const isComponentsAvailable = componentsReferences.length > 0;
  const isSpareAvailable = sparePartsReferences.length > 0;
  const bundleData = useMemo(
    () => componentsProducts?.filter((item: any) => componentsReferences.includes(item.ItemCode)),
    [componentsProducts, componentsReferences],
  );

  const sparePartsData = useMemo(
    () => componentsProducts?.filter((item: any) => sparePartsReferences.includes(item.ItemCode)),
    [componentsProducts, sparePartsReferences],
  );

  const toiletCompatibilityData = useMemo(
    () => componentsProducts?.filter((item: any) =>
      toiletSeatPancompatibilityReferences.includes(item.ItemCode) ||
      toiletSeatPanSuitableReferences.includes(item.ItemCode)
    ),
    [componentsProducts, toiletSeatPancompatibilityReferences, toiletSeatPanSuitableReferences],
  );

  const componentsProd = bundleData?.length !== 0;
  const sparepartsProd = sparePartsData?.length !== 0;
  const toiletCompatibilityProd = toiletCompatibilityData?.length !== 0;
  const category = product?.categories[0]?.name;
  const normalizedCategory = category?.toLowerCase();
const normalizedName = itemName?.toLowerCase() || '';
  const isSuiteCategory = [
    'bidet suites',
    'toilet suites',
    'care toilets',
  ].includes(normalizedCategory);
  
  const isSeat = normalizedName.includes('seat');
  const isPan = normalizedName.includes('pan');
  const compatibilityName = useMemo(() => {
    if (isSuiteCategory) {
      if (isSeat) return 'Compatible Toilet Pans';
      if (isPan) return 'Compatible Toilet Seats';
      return 'Compatibility';
    }
    if (category === 'Toilet Seats') return 'Compatible Toilet Pans';
    if (category === 'Toilet Pans') return 'Compatible Toilet Seats';
     return 'Compatibility';
  }, [category, isSuiteCategory, isSeat, isPan]);
  const sidebarSections = useMemo(() => {
    const sections = [
      {
        name: 'Product overview',
        icon: (
          <svg width="32" height="32" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M38.4167 45.6667H36V36H33.5833M36 26.3333H36.0242M57.75 36C57.75 48.0122 48.0122 57.75 36 57.75C23.9878 57.75 14.25 48.0122 14.25 36C14.25 23.9878 23.9878 14.25 36 14.25C48.0122 14.25 57.75 23.9878 57.75 36Z"
              stroke="#666666"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
        value: 'product-overview',
      },
      {
        name: 'Components',
        icon: (
          <svg width="32" height="32" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M7 7H10.8667C11.8922 7 12.8757 7.40738 13.6008 8.13252C14.326 8.85766 14.7333 9.84116 14.7333 10.8667V45.0222C14.7333 46.0477 15.1407 47.0312 15.8659 47.7564C16.591 48.4815 17.5745 48.8889 18.6 48.8889H65M41.8 48.8889H22.4667V31.0249C22.4667 30.2934 22.7573 29.5918 23.2745 29.0745C23.7918 28.5573 24.4934 28.2667 25.2249 28.2667H39.0418C39.404 28.2667 39.7627 28.338 40.0973 28.4766C40.4319 28.6152 40.736 28.8184 40.9921 29.0745C41.2483 29.3307 41.4514 29.6347 41.59 29.9694C41.7287 30.304 41.8 30.6627 41.8 31.0249V48.8889ZM41.8 48.8889L61.1333 48.8889V34.3502C61.0433 33.6985 60.7075 33.1059 60.1947 32.6937C59.6819 32.2816 59.0309 32.0811 58.3751 32.1333H44.5582C43.9024 32.0811 43.2514 32.2816 42.7386 32.6937C42.2258 33.1059 41.89 33.6985 41.8 34.3502V48.8889ZM32.1333 19.8889V17.3111C32.1333 16.2856 32.5407 15.3021 33.2659 14.577C33.991 13.8518 34.9745 13.4444 36 13.4444H47.6C48.6255 13.4444 49.609 13.8518 50.3341 14.577C51.0593 15.3021 51.4667 16.2856 51.4667 17.3111V24.4M10.8667 61.1333C10.8667 62.1588 11.274 63.1423 11.9992 63.8675C12.7243 64.5926 13.7078 65 14.7333 65C15.7588 65 16.7423 64.5926 17.4675 63.8675C18.1926 63.1423 18.6 62.1588 18.6 61.1333C18.6 60.1078 18.1926 59.1243 17.4675 58.3992C16.7423 57.674 15.7588 57.2667 14.7333 57.2667C13.7078 57.2667 12.7243 57.674 11.9992 58.3992C11.274 59.1243 10.8667 60.1078 10.8667 61.1333ZM53.4 61.1333C53.4 62.1588 53.8074 63.1423 54.5325 63.8675C55.2577 64.5926 56.2412 65 57.2667 65C58.2922 65 59.2757 64.5926 60.0008 63.8675C60.726 63.1423 61.1333 62.1588 61.1333 61.1333C61.1333 60.1078 60.726 59.1243 60.0008 58.3992C59.2757 57.674 58.2922 57.2667 57.2667 57.2667C56.2412 57.2667 55.2577 57.674 54.5325 58.3992C53.8074 59.1243 53.4 60.1078 53.4 61.1333Z"
              stroke="#666666"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
        value: 'components-info',
      },
      {
        name: 'Spare Parts',
        icon: (
          <svg width="32" height="32" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M7.52442 7.52442C5.20187 12.4949 11.0565 25.3968 16.0116 30.3518C19.8161 34.4489 26.2546 34.4489 30.3519 30.3518C34.4492 26.5472 34.1565 20.1087 30.3519 16.0115M7.52442 7.52442L16.0116 16.0115C20.1088 11.9143 26.5473 11.9143 30.3519 16.0115M7.52442 7.52442C12.4949 5.20188 25.3968 11.0565 30.3519 16.0115M30.3564 30.3565L35.1537 35.1536M23.8241 33.3993L29.9446 43.6384M33.4001 23.8235L43.6392 29.944M23.1816 23.7192C22.8849 23.7192 22.6444 23.4786 22.6444 23.1819C22.6444 22.8852 22.8849 22.6447 23.1816 22.6447C23.4783 22.6447 23.7189 22.8851 23.7189 23.1818C23.7189 23.4785 23.4783 23.7192 23.1816 23.7192ZM46.219 55.5085C47.8309 59.0006 46.219 63.0296 42.7271 64.3728C39.2352 65.9844 35.4746 64.3728 33.8627 60.8809L28.8888 50.0116C27.6374 47.2774 28.874 44.0482 31.6317 42.8494L34.1327 41.7619C36.817 40.5949 39.9417 41.7917 41.16 44.4532L46.219 55.5085ZM55.5086 46.2192C59.0005 47.8308 63.0298 46.2192 64.3727 42.727C65.9846 39.2351 64.3727 35.4745 60.8808 33.8629L50.0117 28.8888C47.2772 27.6375 44.0483 28.8741 42.8492 31.6317L41.762 34.1327C40.5947 36.8172 41.7915 39.9419 44.453 41.1599L55.5086 46.2192Z"
              stroke="#666666"
              strokeWidth="4"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
        value: 'spare-parts',
      },
      {
        name: compatibilityName,
        icon: (
          <svg
            width={32}
            height={32}
            viewBox="0 0 65 80"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
          >
            <rect width={65} height={80} fill="url(#pattern0_7977_14934)" />
            <defs>
              <pattern
                id="pattern0_7977_14934"
                patternContentUnits="objectBoundingBox"
                width={1}
                height={1}
              >
                <use
                  xlinkHref="#image0_7977_14934"
                  transform="matrix(0.0180995 0 0 0.0147059 -0.124434 0)"
                />
              </pattern>
              <image
                id="image0_7977_14934"
                width={69}
                height={68}
                preserveAspectRatio="none"
                xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEUAAABECAMAAADgDwi+AAAAAXNSR0IArs4c6QAAAXpQTFRFAAAAIhEiIiAiIx8fJCAgIR8fIx8gIx8gIhwcIh4gIx8gIx8gJB8fIx4gFxcXIx4fIiIiIx4fJCEhJB8gIx4gHBwcIh8fIyAgJh8fJCQkGRkZIx4gIx8gAAAAIx8fJCAhIR0dIh4gIx8gIh8gIx8fJCQkKioqIx8fIx8gJR8fIx8fIh0dIhERIh8fIx8gIh8fIx8hIiAgIx8fIx8gICAgIx8fIyAgJSAgIh4gJB8gIh0gIx8gGRkZICAgIiIiGxsbIyAgIh8fIx8hIR4gKBsbIx8gJCEhIh4eIx8gHx8fIx8fIh8gAAAAIx4fIx8fHRQdIh8iIyAgIh4fIx8gJB4gJB4eIx8gJB4hIx8gICAgIyAhIyAgQEBAMzMzIyEhIh4fISEhHBwcIh4fIyAgIB0gIx8gIx4gIx8gJCQkIx4gIh8gIyAgJB4gIyAgIyAgIx4eIBggIyAgIR8fIx4gIxcjIyAhIyAhHBwcIx8gIx8gIx8gKioqIx4eICAg9wGq8QAAAH50Uk5TAA9pc0B0//4lh/DGMbEL+zXMVpahCWJnSgcUZvkCm+o9gPLX3RwGpvVavzUPjfr3+2FJ9yDFkWDizWjZCjgPE5C7u5ET6l1E/SGEzwHLkxpLYPS1sSv8Vt0Q6nYEBV79HxK8ZkfAsKcO3d/hh+KgOyChfJ8WyqEktr6vDG0I/EXeLQAAAUFJREFUWIXt1FdTwkAYheGoeCIW7N2osffeIdgVFbH3AnbF3vt/lxAQLsg6sx8z3ux7szuTk+dyJUkkEoVLSEwyslCUZISSUwiKFalpejLSSUqGftgyhSIUoQglzkpWdk7wzM3LJyjxyVZQyKrIWBWXsJVSMCsL/q6UV1QyFRVV1abVALWBTV090NDIVprMPzZDbgkcrWhDe0cnr9KFbkWSeuTePvQPDPIqQ7Drh8WhQnMO8yojGA2vNIbxhzKGcboyMYkpujIN1wxdmcWcm67Mw/O74lYWXFikK0tYdtCVFaxGVrzK2jo26IoHm06ysrWNnaiVtrvHo+zD6zNuB4dHKo5PTnkUO85Ct/ML+HGJK6ZybY3VDby34c2dP/B23isM5cH0yX2MjJ6e8eJmINLr23vsPnxRq8+vbxYiEon+sR/IMkR3jXKqagAAAABJRU5ErkJggg=="
              />
            </defs>
          </svg>
        ),
        value: 'toilet-seat-compatibility',
      },
      {
        name: 'Features',
        icon: (
          <svg width="32" height="32" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M46.3571 13.2143H52.5714C53.6702 13.2143 54.7239 13.6507 55.5009 14.4277C56.2778 15.2046 56.7143 16.2584 56.7143 17.3571V58.7857C56.7143 59.8845 56.2778 60.9382 55.5009 61.7151C54.7239 62.4921 53.6702 62.9286 52.5714 62.9286H19.4286C18.3298 62.9286 17.2761 62.4921 16.4991 61.7151C15.7222 60.9382 15.2857 59.8845 15.2857 58.7857V17.3571C15.2857 16.2584 15.7222 15.2046 16.4991 14.4277C17.2761 13.6507 18.3298 13.2143 19.4286 13.2143H25.6428M46.3571 13.2143C46.3571 10.9262 44.5023 9.07141 42.2143 9.07141H29.7857C27.4977 9.07141 25.6428 10.9262 25.6428 13.2143M46.3571 13.2143L46.3571 15.2857C46.3571 17.5737 44.5023 19.4286 42.2143 19.4286H29.7857C27.4977 19.4286 25.6428 17.5737 25.6428 15.2857L25.6428 13.2143M25.6428 29.7857H46.3571M25.6428 40.1428H46.3571M25.6428 50.5H46.3571"
              stroke="#666666"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
        value: 'features-benefits',
      },
      // {
      //   name: 'Related Products',
      //   icon: (
      //     <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6">
      //       <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75" />
      //     </svg>
      //   ),
      //   value: 'related-products',
      // },

    ];

    return sections.filter((section) => {
      if (section.value === 'components-info') {
        if (!isComponentsAvailable || !componentsProd) return false;
      }
      if (section.value === 'spare-parts') {
        if (!isSpareAvailable || !sparepartsProd) return false;
      }
      if (section.value === 'toilet-seat-compatibility') {
        if (!toiletCompatibilityData || !toiletCompatibilityProd) return false;
      }
      if (section.value === 'features-benefits') {
        if (productType) return false;
      }
      return true;
    });
  }, [isComponentsAvailable, isSpareAvailable, toiletCompatibilityData, componentsProd, sparepartsProd, toiletCompatibilityProd, compatibilityName]);

  const componentsInventory = useMemo(
    () =>
      componentsReferences
        ?.map((code: any) => inventoryData?.find((item: any) => item?.itemcode === code))
        .filter((item: undefined) => item !== undefined),
    [componentsReferences, inventoryData],
  );

  const sparePartsInventory = useMemo(
    () =>
      sparePartsReferences
        ?.map((code: any) => inventoryData?.find((item: any) => item?.itemcode === code))
        .filter((item: undefined) => item !== undefined),
    [sparePartsReferences, inventoryData],
  );

  const toiletCompatibilityInventory = useMemo(
    () =>
      [...toiletSeatPancompatibilityReferences, ...toiletSeatPanSuitableReferences]
        ?.map((code: any) => inventoryData?.find((item: any) => item?.itemcode === code))
        .filter((item: undefined) => item !== undefined),
    [toiletSeatPancompatibilityReferences, toiletSeatPanSuitableReferences, inventoryData],
  );

  const productInventory = useMemo(
    () => inventoryData?.find((item: any) => item.itemcode === itemCode),
    [inventoryData, itemCode],
  );

  const colour = product.colors?.[0] || variant?.attributes?.colour;
  const renderVariationSelector = !isDesktop && true;
  const variationUI = useMemo(() => {
    if (productType) return null;
    return (
      <VariantSelector
        variants={variants}
        currentVariantIndex={variant}
        handleColorClick={handleColorClick}
      />
    );
  }, [productType, variants, handleColorClick, variant]);

  const relatedProducts = useProduct({
    categories: product?.categories?.map((category: any) => category.categoryId) as string[],
    limit: 8,
  });
  const handleAddToProject = (prodToUse?: any) => {
    const targetProduct = prodToUse || product;
    const sku = targetProduct?.sku || targetProduct?.variants?.[0]?.sku || variant?.attributes?.['product-code'] || itemCode;
    setOpenAddToProject(true);
    setProductToAdd({ name: targetProduct?.name, sku });
  };
const hasOutOfStockProductPart = useMemo(() => {
  const displayedProducts = [
    ...(bundleData ?? []),
    // ...(sparePartsData ?? []),
    // ...(toiletCompatibilityData ?? [])
  ];

  return displayedProducts.some((product: any) => {
    // Find inventory for the product shown on frontend
    const inventory = inventoryData?.find(
      (item: any) => item?.itemcode === product?.ItemCode
    );

    // Inventory not found → ignore this product
    if (!inventory) {
      return false;
    }

    const warehouses = inventory?.warehouses ?? [];

    const warehouseData = warehouses.find(
      (wh: any) =>
        wh?.code?.trim()?.toUpperCase() ===
        warehouse?.trim()?.toUpperCase()
    );

    // Inventory exists but selected warehouse doesn't exist
    if (!warehouseData) {
      return true;
    }

    const quantity = Number(warehouseData?.quantity);

    return Number.isNaN(quantity) || quantity <= 0;
  });
}, [
  bundleData,
  sparePartsData,
  toiletCompatibilityData,
  inventoryData,
  warehouse,
]);
console.log('hasOutOfStockProductPart',hasOutOfStockProductPart)
  return (
    <>
      <head>
        <title key="title">{product?.title}</title>
        <meta name="description" content={product?.metaDescription} key="description" />
        <meta name="keywords" content={product?.metaKeywords} />
      </head>
      {loading && <Spinner />}
      {loading && <div className="overlay fixed left-0 top-0 z-50 h-full w-full bg-gray-800 opacity-75"></div>}
      <div className="px-4 md:px-8 lg:px-10 ">
        <Header
          product={product}
          variant={variant}
          itemCode={itemCode}
          itemName={itemName}
          productInventory={productInventory}
          customerQuantityPrice={customerQuantityPrice}
          deliveryEstimate={deliveryEstimate}
          isLoading={isLoading}
          isPriceLoading={isPriceLoading}
          variationUI={variationUI}
          productType={productType}
          isBuilderRange={isBuilderRange}
          setQuantity={setQuantity}
          quantityPrice={quantityPrice}
          quantity={quantity}
          variantKey={variant?.key}
          quantityPriceLoading={quantityPriceLoading}
          setPostalCode={setPostalCode}
          postalCode={postalCode}
          loadingDeliverEstimate={loadingDeliverEstimate}
          warehouse={warehouse}
          warehouseqty={warehouseqty}
          handleAddToProject={handleAddToProject}
          hasOutOfStockProductPart={hasOutOfStockProductPart}
        />
        <div className="grid grid-cols-12 gap-4 pdp-block">
          <div className="col-span-3 hidden xl:block relative">
            <div className="sticky top-[150px] z-10 ">
              <SidebarPDP isBundleAvailable={isComponentsAvailable} sidebarSections={sidebarSections} />
            </div>
          </div>
          <div className="col-span-12 xl:col-span-9">
            <Gallery
              images={product.images}
              sku={variant?.attributes?.['product-code'] ?? product.sku}
              color={colour}
            />
            <div className="lg:hidden mt-4">
              <DeliveryDetails
                productInventory={productInventory}
                customerQuantityPrice={customerQuantityPrice}
                product={product}
                deliveryEstimate={deliveryEstimate}
                loading={isLoading}
                isPriceLoading={isPriceLoading}
                productType={productType}
                isBuilderRange={isBuilderRange}
                setQuantity={setQuantity}
                quantityPrice={quantityPrice}
                quantity={quantity}
                variantKey={variant?.key}
                itemCode={itemCode}
                quantityPriceLoading={quantityPriceLoading}
                setPostalCode={setPostalCode}
                postalCode={postalCode}
                loadingDeliverEstimate={loadingDeliverEstimate}
                warehouse={warehouse}
                hasOutOfStockProductPart={hasOutOfStockProductPart}
                
              />
            </div>
            <div className="lg:hidden">
              <SidebarMobilePDP sidebarSections={sidebarSections} />
            </div>
            <div className="product-overview w-full bg-[#F3EDE9] pb-20">
              <div className="mt-10 lg:mt-20">
                {/* {!productType ? ( */}
                <ProductSpecificationDetails
                  setLoading={setLoading}
                  attributes={variant?.attributes}
                  product={product}
                  variants={variants}
                />
              </div>
            </div>
            {isBundleLoading ? (
              <div className="flex items-center justify-center">
                <Loader />
              </div>
            ) : (
              <>
                <div className="components-info" id="components-info">
                  {bundleData?.length > 0 && (
                    <BundleProducts bundleData={bundleData} componentsInventory={componentsInventory} warehouse={warehouse} isPrototype={isPrototype} />
                  )}
                </div>

                <div className="spare-parts" id="spare-parts">
                  {sparePartsData?.length > 0 && (
                    <SpareParts sparePartsData={sparePartsData} sparePartsInventory={sparePartsInventory}  warehouse={warehouse} isPrototype={isPrototype}/>
                  )}
                </div>

                <div className="toilet-compatibility" id="toilet-seat-compatibility">
                  {toiletCompatibilityData?.length > 0 && (
                    <CompatibilityProducts bundleData={toiletCompatibilityData} componentsInventory={toiletCompatibilityInventory} compatibilityName={compatibilityName} warehouse={warehouse} isPrototype={isPrototype} />
                  )}
                </div>

              </>
            )}
            {!productType && (
              <div className="features-benefits mb-[40px]">
                <FeaturesBenefits attributes={variant?.attributes} />
              </div>
            )}
            {/* <div id='related-products' className='related-products relative left-full ml-4 w-screen -translate-x-full bg-[#F3EDE9] pb-[40px] md:pb-[60px] lg:pb-20 md:ml-10 lg:ml-10 xl:ml-20' >
              <ProductSliderAlgolia products={relatedProducts}
                handleAddToProject={handleAddToProject}
              />
            </div> */}

          </div>
        </div>
      </div>
      <AddProductToProject
        open={openAddToProject}
        onCancel={handleCancelAddProduct}
        productToAdd={productToAdd}
      />
    </>
  );
};

export default ProductDetails;
